<template>
  <v-textarea
    ref="agTextAreaRef"
    density="compact"
    variant="solo"
    class="ag-text-area"
    persistent-hint
    no-resize
    persistent-counter
    :hide-details="hideDetails"
    :persistent-placeholder="persistentPlaceholder"
    :disabled="disabled"
    :error-messages="errorMessages"
    :hint="hint"
    :label="localLabel"
    :model-value="localValue"
    :placeholder="placeholder"
    :readonly="readonly"
    :rules="rules"
    :counter="counter"
    :maxlength="maxlength"
    :rows="rows"
    :data-message-color="messageColor"
    @update:model-value="onInput"
  />
</template>

<script>
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';

export default {
  emits: ['update:modelValue', 'click:clear', 'click:append-inner'],
  props: {
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    label: {
      type: [String],
      required: true,
    },
    placeholder: {
      type: [String],
      default: undefined,
    },
    persistentPlaceholder: {
      type: [Boolean],
      default: false,
    },
    hint: {
      type: [String],
      default: undefined,
    },
    hintIcon: {
      type: [Boolean],
      default: false,
    },
    counter: {
      type: [Number, String],
      default: undefined,
    },
    persistentCounter: {
      type: [Boolean],
      default: false,
    },
    maxlength: {
      type: [Number, String],
      default: undefined,
    },
    disabled: {
      type: [Boolean],
      default: false,
    },
    readonly: {
      type: [Boolean],
      default: false,
    },
    rules: {
      type: [Array],
      default: () => [],
    },
    errorMessages: {
      type: [String],
      default: undefined,
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
    rows: {
      type: [Number, String],
      default: 5,
    },
    messageColor: {
      type: [String],
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
  },
  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    campoObrigatorio() {
      return this.rules.some((rule) => rule?.name?.toLowerCase().includes('required'));
    },
    localLabel() {
      return `${this.label}${this.campoObrigatorio ? ' *' : ''}`;
    },
  },
  watch: {
    maxlength(val) {
      if (val) this.onInput(this.localValue);
    },
  },
  methods: {
    onInput(newVal) {
      let value = newVal;
      if (this.maxlength && newVal && newVal.length > this.maxlength) {
        value = newVal.substring(0, this.maxlength);
      }
      this.$refs.agTextAreaRef.lazyValue = value;
      this.$emit('update:modelValue', value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/messages.scss';

::v-deep(.v-label) {
  opacity: 1;
  top: 16px;
  color: $brand-color-primary-medium;

  &.v-field-label--floating {
    transform: translateY(12px) !important;
  }
}

::v-deep(.v-field) {
  opacity: 1;
  background-color: $neutral-color-low-light;
  border: solid 1px $neutral-color-high-light;
  box-shadow: none;
  overflow: hidden;

  &:hover {
    background-color: $brand-color-highlight-light;
  }

  &:active {
    border-color: $brand-color-primary-medium;
  }

  &.v-field--disabled {
    background-color: $neutral-color-low-dark;
    border-color: $neutral-color-low-dark;
    pointer-events: visible;
  }

  /* Sobrescreve o efeito "fade" do vuetify */
  textarea {
    padding: 40px 16px 16px;
    color: $brand-color-primary-medium;

    mask-image:
      linear-gradient(to bottom, transparent, transparent 32px, black 40px),
      linear-gradient(to left, black 16px, transparent 1px);
    mask-size: 100% 100%;
    mask-position:
      0 0,
      100% 0;
    mask-repeat: no-repeat, no-repeat;
  }
}

/* Impede o counter de quebrar quando o hint for muito longo */
::v-deep(.v-input__details) .v-counter {
  white-space: nowrap;
}
</style>
